import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Sidebar from "../../components/Sidebar";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import theme from "../../themes";
import { useAppDispatch } from "../../store";
import {
  assignAgentToLineThunk,
  getLinesThunk,
} from "../../store/actions/line.action";
import { getAgentsThunk } from "../../store/actions/agent.action";
import ToastMessage from "../../utils/toastMessage";
import { Input, Select, Table, Card, Space, Empty, App } from "antd";
import { ILineResponse, LineStatus } from "../../models/line.model";
import { IAgent } from "../../models/agent.model";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";

const { Option } = Select;

interface PhoneNumberWithAgent extends ILineResponse {
  attached_inbound_agent?: string;
}

const Inbound: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const toast = ToastMessage(message);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumberWithAgent[]>([]);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const fetchPhoneNumbers = async () => {
    try {
      const res = await dispatch(
        getLinesThunk({
          limit: pageSize,
          page: currentPage,
          line_number: debouncedSearchTerm,
          status: [LineStatus.ACTIVE, LineStatus.MESSAGE_SERVICE_PENDING],
          sort: {},
        })
      ).unwrap();
      setPhoneNumbers(res.data.lines || []);
      setTotalPages(res.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAgents = async () => {
    try {
      const res = await dispatch(
        getAgentsThunk({
          limit: 999,
          page: 1,
          keyword: "",
          direction: "inbound",
        })
      ).unwrap();
      setAgents(res.data.agents || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAgentAssignment = async (
    phoneNumberId: string,
    agentId: string
  ) => {
    try {
      setLoading(true);
      await dispatch(
        assignAgentToLineThunk({ id: phoneNumberId, agentId })
      ).unwrap();

      fetchPhoneNumbers();
      toast.success(
        t("inbound.agentAssignedSuccessfully"),
        5,
        <CheckCircleIcon />
      );
    } catch (error) {
      console.error(error);
      toast.error(t("inbound.failedToAssignAgent"), 5);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchInput(value);
    setCurrentPage(1);
  };

  // Debounce search input to reduce API calls while typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchInput.trim());
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchPhoneNumbers(), fetchAgents()]).finally(() => {
      setLoading(false);
    });
  }, [currentPage, debouncedSearchTerm]);

  const columns = [
    {
      title: t("inbound.phoneNumber"),
      dataIndex: "line_number",
      key: "line_number",
      width: "50%",
      render: (text: string) => <PhoneNumberLink>{text}</PhoneNumberLink>,
    },
    {
      title: t("inbound.agent"),
      dataIndex: "attached_inbound_agent",
      key: "attached_inbound_agent",
      width: "50%",
      render: (agentId: string, record: any) => (
        <Select
          placeholder={t("inbound.selectAgent")}
          value={agents.find((agent) => agent._id === agentId)?.agent_name}
          onChange={(value) => handleAgentAssignment(record._id, value)}
          style={{ width: "100%" }}
          size="large"
        >
          {agents.map((agent) => (
            <Option key={agent._id} value={agent._id}>
              {agent.agent_name}
            </Option>
          ))}
        </Select>
      ),
    },
  ];

  return (
    <FlexRow>
      <MainContent>
        <Title>{t("inbound.title")}</Title>

        <CardWrapper>
          <Space
            style={{
              marginBottom: 16,
              width: "100%",
              justifyContent: "flex-end",
              display: "flex",
            }}
          >
            <SearchWrapper>
              <SearchIcon />
              <CustomSearch
                placeholder={t("inbound.searchPhoneNumber")}
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </SearchWrapper>
          </Space>
          <StyledTable
            dataSource={phoneNumbers.map((phone) => ({
              key: phone._id,
              ...phone,
            }))}
            columns={columns}
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalPages * pageSize,
              position: ["bottomCenter"],
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
            }}
            rowKey="key"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <EmptyStateTitle>
                      {t("inbound.noPhoneNumbers")}
                    </EmptyStateTitle>
                  }
                />
              ),
            }}
          />
        </CardWrapper>
      </MainContent>
    </FlexRow>
  );
};

const FlexRow = styled.div`
  display: flex;
  height: 100vh;
  background-color: ${theme.colors.grey3};
`;

const MainContent = styled.div`
  flex: 1;
  padding: 52px 72px;
  gap: 32px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

const CardWrapper = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
`;

const SearchWrapper = styled.div`
  width: 240px;
  display: flex;
  background-color: ${theme.colors.grey4};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 0px 8px;
  align-items: center;
  box-sizing: border-box;
  gap: 8px;
`;

const CustomSearch = styled(Input)`
  border-radius: 12px;
  height: 40px;
  font-size: 16px;
  border: none;
  box-shadow: none;
  background-color: ${theme.colors.grey4};
  padding: 0;

  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
    background-color: ${theme.colors.grey4};
  }
`;

const PhoneNumberLink = styled.span`
  color: ${theme.colors.blue1};
  font-weight: 500;
`;

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: white;
    border-bottom: none;
    padding: 10px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.grey1};
    font-weight: 500;
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    border-bottom: none;
  }

  .ant-table-tbody > tr:nth-child(odd) {
    background-color: ${theme.colors.grey3};
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: ${theme.colors.grey4};
  }

  .ant-table table {
    border-spacing: 0 4px;
    border-collapse: separate;
  }

  .ant-table-tbody > tr > td:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }
`;

export default Inbound;
